/**
 * Réglages de génération de donnes pour l'entraînement libre.
 * Persistés en localStorage et convertis en TrainingDealConstraints.
 */

import type { HandQuality, TrainingDealConstraints } from '../types/app';

export const TRAINING_DEAL_SETUP_KEY_PREFIX = 'wt_neo_training_deal_setup';

export const CHAVEY_CRANK_SCORES = [0, 40, 56, 71, 81] as const;
export const CHAVEY_CRANK_LABELS = ['Hasard pur', 'Petite', 'Garde', 'Garde Sans', 'Garde Contre'] as const;
export const CHAVEY_CRANK_SHORT = ['Hasard', 'Petite', 'Garde', 'G. Sans', 'G. Contre'] as const;
const CHAVEY_CUSTOM_ABSOLUTE_MAX = 100;
export const CHAVEY_CUSTOM_SLIDER_MAX =
  CHAVEY_CRANK_SCORES[4] + Math.round((CHAVEY_CUSTOM_ABSOLUTE_MAX - CHAVEY_CRANK_SCORES[4]) / 2);

export type ChaveySeuil = 0 | 1 | 2 | 3 | 4;

export interface TrainingDealSetupValue {
  /** Siège preneur cible (0 = Sud / vous). Ignoré si force désactivée. */
  taker: number;
  /** Mode curseur personnalisé (score brut). */
  chaveyCustom: boolean;
  /** Cran classique 0–4 (0 = hasard, pas de contrainte preneur). */
  chaveySeuil: ChaveySeuil;
  /** Score Chavey brut en mode personnalisé. */
  chaveyMinScore: number;
  /** Force les enchères : les autres passent, le preneur annonce le contrat. */
  guaranteeTaker: boolean;
  /** Qualité de main souhaitée par siège (avancé). */
  handQualities: Record<number, HandQuality>;
}

export const DEFAULT_TRAINING_DEAL_SETUP: TrainingDealSetupValue = {
  taker: 0,
  chaveyCustom: false,
  chaveySeuil: 0,
  chaveyMinScore: 0,
  guaranteeTaker: false,
  handQualities: {},
};

export function clampChaveyCustomScore(score: number): number {
  if (score <= 0) return 0;
  return Math.min(CHAVEY_CUSTOM_SLIDER_MAX, Math.round(score));
}

export function chaveySeuilToScore(seuil: ChaveySeuil): number {
  return CHAVEY_CRANK_SCORES[seuil];
}

export function chaveyScoreToSeuil(score: number): ChaveySeuil {
  if (score <= 0) return 0;
  if (score >= 81) return 4;
  if (score >= 71) return 3;
  if (score >= 56) return 2;
  if (score >= 40) return 1;
  return 1;
}

export function isClassicChaveyCrank(score: number): boolean {
  return (CHAVEY_CRANK_SCORES as readonly number[]).includes(score);
}

export function chaveyLabelForScore(score: number, custom = false): string {
  const passLabel = custom ? 'Passe' : CHAVEY_CRANK_LABELS[0];
  if (score <= 0) return passLabel;
  if (score >= 81) return CHAVEY_CRANK_LABELS[4];
  if (score >= 71) return CHAVEY_CRANK_LABELS[3];
  if (score >= 56) return CHAVEY_CRANK_LABELS[2];
  if (score >= 40) return CHAVEY_CRANK_LABELS[1];
  return passLabel;
}

/**
 * Validates hand quality constraints against the actual card pool.
 * Based on the resources required by constructConstrainedHands.
 */
export function validateHandQualities(
  qualities: Record<number, HandQuality>,
): { valid: boolean; error?: string } {
  const values = Object.values(qualities).filter(q => q !== 'random');
  if (values.length === 0) return { valid: true };

  const passeCount = values.filter(q => q === 'passe').length;
  if (passeCount > 2) {
    return {
      valid: false,
      error: 'Maximum 2 joueurs en "passe" (les atouts et rois doivent être distribués aux autres)',
    };
  }
  if (passeCount > 0 && values.filter(q => q !== 'passe').length + (4 - values.length) < 2) {
    return {
      valid: false,
      error: 'Il faut au moins 2 joueurs non-passe pour absorber les atouts et rois',
    };
  }

  let need21 = 0;
  let needPetit = 0;
  let needExcuse = 0;
  let needHigh = 0;
  let needLow = 0;
  let needKings = 0;

  for (const q of values) {
    switch (q) {
      case 'garde_contre':
        need21++;
        needPetit++;
        needExcuse++;
        needHigh += 5;
        needLow += 3;
        needKings += 2;
        break;
      case 'garde_sans':
        need21++;
        needExcuse++;
        needHigh += 3;
        needLow += 3;
        needKings += 1;
        break;
      case 'garde':
        needHigh += 2;
        needLow += 3;
        needKings += 1;
        break;
      case 'petite':
        needLow += 3;
        break;
      case 'passe':
        break;
    }
  }

  if (need21 > 1) {
    return {
      valid: false,
      error: 'Le 21 ne peut aller qu\'à un seul joueur (garde sans / garde contre exclusifs)',
    };
  }
  if (needPetit > 1) {
    return {
      valid: false,
      error: 'Le Petit ne peut aller qu\'à un seul joueur (une seule garde contre possible)',
    };
  }
  if (needExcuse > 1) {
    return {
      valid: false,
      error: 'L\'Excuse ne peut aller qu\'à un seul joueur (garde sans / garde contre exclusifs)',
    };
  }
  if (needHigh > 6) {
    return { valid: false, error: 'Pas assez d\'atouts majeurs (15–20) pour cette configuration' };
  }
  if (needLow > 13) {
    return { valid: false, error: 'Pas assez d\'atouts mineurs (2–14) pour cette configuration' };
  }
  if (needKings > 4) {
    return { valid: false, error: 'Pas assez de Rois pour cette configuration' };
  }

  return { valid: true };
}

export function isTakerConstraintActive(value: TrainingDealSetupValue): boolean {
  if (value.chaveyCustom) {
    return value.chaveyMinScore > 0;
  }
  return value.chaveySeuil > 0;
}

export function toTrainingDealConstraints(
  value: TrainingDealSetupValue,
): TrainingDealConstraints | undefined {
  const out: TrainingDealConstraints = {};
  let hasAny = false;

  if (isTakerConstraintActive(value)) {
    out.taker = value.taker;
    if (value.chaveyCustom) {
      const score = clampChaveyCustomScore(value.chaveyMinScore);
      out.takerMinBidScore = score;
      out.takerContract = chaveyScoreToSeuil(score);
    } else {
      out.takerContract = value.chaveySeuil;
    }
    if (value.guaranteeTaker && (out.takerContract ?? 0) > 0) {
      out.guaranteeTaker = true;
    }
    hasAny = true;
  }

  const qualities: Record<number, HandQuality> = {};
  for (const [seatStr, q] of Object.entries(value.handQualities)) {
    if (q && q !== 'random') {
      qualities[Number(seatStr)] = q;
    }
  }
  if (Object.keys(qualities).length > 0) {
    out.handQualities = qualities;
    hasAny = true;
  }

  return hasAny ? out : undefined;
}

function isHandQuality(v: unknown): v is HandQuality {
  return (
    v === 'random'
    || v === 'passe'
    || v === 'petite'
    || v === 'garde'
    || v === 'garde_sans'
    || v === 'garde_contre'
  );
}

export function loadTrainingDealSetup(pseudo: string): TrainingDealSetupValue {
  if (!pseudo) return { ...DEFAULT_TRAINING_DEAL_SETUP, handQualities: {} };
  try {
    const raw = localStorage.getItem(`${TRAINING_DEAL_SETUP_KEY_PREFIX}:${pseudo}`);
    if (!raw) return { ...DEFAULT_TRAINING_DEAL_SETUP, handQualities: {} };
    const parsed = JSON.parse(raw) as Partial<TrainingDealSetupValue>;
    const handQualities: Record<number, HandQuality> = {};
    if (parsed.handQualities && typeof parsed.handQualities === 'object') {
      for (const [k, v] of Object.entries(parsed.handQualities)) {
        if (isHandQuality(v)) handQualities[Number(k)] = v;
      }
    }
    const seuil = Number(parsed.chaveySeuil);
    const chaveySeuil = ([0, 1, 2, 3, 4].includes(seuil) ? seuil : 0) as ChaveySeuil;
    const taker = Number(parsed.taker);
    return {
      taker: Number.isInteger(taker) && taker >= 0 && taker <= 3 ? taker : 0,
      chaveyCustom: Boolean(parsed.chaveyCustom),
      chaveySeuil,
      chaveyMinScore: clampChaveyCustomScore(Number(parsed.chaveyMinScore) || 0),
      guaranteeTaker: Boolean(parsed.guaranteeTaker),
      handQualities,
    };
  } catch {
    return { ...DEFAULT_TRAINING_DEAL_SETUP, handQualities: {} };
  }
}

export function saveTrainingDealSetup(pseudo: string, value: TrainingDealSetupValue): void {
  if (!pseudo) return;
  try {
    localStorage.setItem(`${TRAINING_DEAL_SETUP_KEY_PREFIX}:${pseudo}`, JSON.stringify(value));
  } catch {
    /* quota / private mode */
  }
}
